import { createUseFetch, createUseFetchAction } from '@ringdna/client/src/react/createUseFetch'
import { UserLicenses } from '@ringdna/common/src/types/user-settings'
import { HttpMethods } from '@ringdna/client/src/constants'

const API_BASE = '/api/v2/app/users'

export const useUserSettings = createUseFetch<{ [key: string]: any }, { meta: { id: number } }>({
  key: 'fetch-user-settings',
  path: params => `${API_BASE}/${params.meta.id}`
})

export type PicklistAdminUser = {
  id: number
  displayName: string
  settings: {
    sendInvalidNotification: boolean
  }
}

export const useAdminUsers = createUseFetch<PicklistAdminUser[], { meta: { id: number } }>({
  key: 'fetch-admin-users',
  path: params => `${API_BASE}/adminUsers?accountId=${params.meta.id}`,
  independent: true
})

type Pager = {
  'pager.pageSize': number
  'pager.currentPage': number
  'pager.sortBy'?: string | null
  'pager.sortDirection'?: string | null
}

export type GetPageParam = {
  query: {
    accountId: number | null
    userStatus: string
    globalFilter: string
    dialerUsers?: boolean
    cai?: boolean
    momentsUsers?: boolean
    guidedSellingUsers?: boolean
    nylasUsers?: boolean
    adminUsers?: boolean
  } & Pager
}

export type User = {
  id: number
  displayName: string
  email: string
  licenses: UserLicenses
  loginDate: number
  sfUserId: string
  isDeleted: boolean
}

export type UsersResponse = {
  results: User[]
  total: number
}

export const useUsers = createUseFetch<UsersResponse, GetPageParam>({
  key: 'fetch-users',
  independent: true,
  path: API_BASE
})

export type PageParam = {
  query: {
    accountId: number
    globalFilter: string
    teamId: number | null
    profileId: string | null
  } & Pager
}

export const useLicenceManagement = createUseFetch<any, PageParam>({
  key: 'fetch-users-licence-management',
  independent: true,
  path: `${API_BASE}/licenceManagement`
})

export const useUpdateLicenceManagement = createUseFetchAction<void, { body: Record<string, any> }>({
  key: 'post-update-users-licence-management',
  path: `${API_BASE}/licenceManagement`,
  method: HttpMethods.Put
})

export type SFUser = {
  city: string
  country: string
  email: string
  isDeleted: boolean
  name: string
  profile: string
  salesforceId: string
  title: string
  userId: number
  adminLicense: boolean
  dialerLicense: boolean
  calendarLicense: boolean
  gsLicense: boolean
  revenueAILicense: boolean
  caiLicense: boolean
  momentsLicense: boolean
  areaCode: string
  extensionCode: string
  location: Location
}

export type GetSFPageParam = {
  query: {
    globalFilter: string
    displayLicensed: boolean
    profileId?: string
  } & Pager
}

export const useSalesforceUsers = createUseFetch<any, GetSFPageParam>({
  key: 'fetch-salesforce-users',
  path: `${API_BASE}/salesforce`,
  independent: true
})

export const useCreateUser = createUseFetchAction<void, { body: Record<string, any> }>({
  key: 'post-create-user',
  path: API_BASE,
  method: HttpMethods.Post
})

export const useCreateUsersBatch = createUseFetchAction<void, { body: Record<string, any> }>({
  key: 'post-create-users-batch',
  path: `${API_BASE}/salesforce/batch`,
  method: HttpMethods.Post
})

export type Location = {
  id: number
  name: string
}

export const useLocations = createUseFetch<Location[], { meta: { id: number } }>({
  key: 'fetch-locations',
  path: params => `/api/v2/app/locations?accountId=${params.meta.id}`
})

export type AvailableLicenses = {
  adminUserCount: number
  adminUserLimit: number
  caiUserCount: number
  caiUserLimit: number
  dialerUserCount: number
  dialerUserLimit: number
  guidedSellingUserCount: number
  guidedSellingUserLimit: number
  nylasUserCount: number
  nylasUserLimit: number
  momentsUserLimit: number
  momentsUserCount: number
  revenueAIUserCount: number
  revenueAIUserLimit: number
}

export const useAvailableLicenses = createUseFetch<AvailableLicenses, { meta: { id: number } }>({
  key: 'fetch-available-licences',
  path: params => `/api/v2/app/licenses?accountId=${params.meta.id}`
})
